import React, { useState } from "react";
import { createPortal } from "react-dom";
import { IdentityService } from "../services/identity";

interface BecomeReviewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartAuth: () => void; // Open auth without clearing intent
  isLoggedIn?: boolean;
  user?: any; // User object
  onGoToOnboarding?: () => void;
}

const BecomeReviewerModal: React.FC<BecomeReviewerModalProps> = ({
  open,
  onOpenChange,
  onStartAuth,
  isLoggedIn,
  user,
  onGoToOnboarding,
}) => {
  const [status, setStatus] = useState<"in_college" | "graduated" | "other">(
    "in_college"
  );
  const [major, setMajor] = useState("");
  const [university, setUniversity] = useState("");

  if (!open) return null;

  const onSubmit = () => {
    try {
      localStorage.setItem("postLoginIntent", "reviewer");

      // If user is already logged in, add reviewer role immediately
      if (isLoggedIn && user?.id) {
        IdentityService.addRole(user.id, "reviewer");
      }
      localStorage.setItem(
        "reviewerPreAnswers",
        JSON.stringify({ status, major, university })
      );
    } catch (_) {}

    onOpenChange(false);

    if (isLoggedIn) {
      onGoToOnboarding && onGoToOnboarding();
    } else {
      onStartAuth();
    }
  };

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
        <div className="p-6 sm:p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Become a Reviewer
            </h2>
            <p className="text-gray-600 mt-1">
              Tell us a bit about your background to get started.
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                Current status
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  className={`border rounded-lg px-3 py-2 text-sm transition-colors ${
                    status === "in_college"
                      ? "border-black bg-gray-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setStatus("in_college")}
                >
                  In College
                </button>
                <button
                  className={`border rounded-lg px-3 py-2 text-sm transition-colors ${
                    status === "graduated"
                      ? "border-black bg-gray-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setStatus("graduated")}
                >
                  Graduated
                </button>
                <button
                  className={`border rounded-lg px-3 py-2 text-sm transition-colors ${
                    status === "other"
                      ? "border-black bg-gray-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setStatus("other")}
                >
                  Other
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  University (optional)
                </label>
                <input
                  value={university}
                  onChange={(e) => setUniversity(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="e.g., UCLA"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Major or Field (optional)
                </label>
                <input
                  value={major}
                  onChange={(e) => setMajor(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="e.g., Computer Science"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-2">
            <button
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
            <button
              className="bg-black text-white px-5 py-2 rounded-lg hover:bg-gray-800"
              onClick={onSubmit}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default BecomeReviewerModal;
